/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import { fileUploadSources, optionsViewType } from "@Features/portal-settings";
import { formSectionType, getUrlFromField } from "./constants";
import { FormControl, FormHelperText } from "@mui/material";
import _ from "lodash";
import { getProjectTeamsUrl, locationUrl } from "@Services/dataManager";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { saveRemoteFiles } from "@Services/fileDownload";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_WARNING } from "@Services/constants";
import { ValidationWrapperWeb } from "@Features/forms";
import AddURLModal from "./addURLModal";
import { FileManagerAsPopup } from "@Features/files";
import AddGeoLocation from "./addGeoLocationModal";
import useQuestionFields from "./useQuestionFields";
import useCameraCapture from "./useCameraCapture";
import CameraCaptureModals from "./cameraCaptureModals";
import { fieldRenderers, getFieldAns } from "./questionFieldsHelper";
import type {
  FieldAnswerViewProps,
  FieldGeoPoint,
  FieldModalStateArgs,
  FileUploadChangeArgs,
  OpenAddUserModalArgs,
  QuestionFieldRenderProps,
  RemoveAllUploadedFilesArgs,
  RemoveUploadedFileArgs,
  RemoveUserFromListArgs,
  SaveEditedImageArgs,
} from "./questionFieldsHelper";
import type { FieldValidatorHandle } from "../FormValidation/useValidationWrapper";
import type { FormFlowUser, QuestionField } from "@ServerTypes/forms/forms.schema";
import { objectIdString, toObjectIdString } from "@Helpers/schemas/common.schema";
import { RootState } from "@Reducers/index";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import { t, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import { EmbeddedViewer } from "@Features/design-viewer";
import AddUser from "./addUserModal";

type SelectedFormFile = {
  fileVersion: string;
  fileId: string;
  resourceType: string;
  versionNumber: number;
  name: string;
};

/**
 * One entry of the File-Manager popup's selection. Distinct from `FieldFileValue` (a stored answer
 * entry): the manager identifies a file by `hierarchyId`, which is copied to `fileId` on the answer.
 */
type FileManagerSelection = {
  fileVersion?: string;
  hierarchyId?: string;
  resourceType?: string;
  versionNumber?: number;
  name?: string;
};

/** A project Team row as the look-up endpoint returns it. */
type TeamRow = { _id: string; name: string };

/** A project Location row as the look-up endpoint returns it. */
type LocationRow = { _id: string; name: string; parentId?: string | null };

/**
 * A stored answer, or one row of a table section, read as a map. `answerSchema.value` and
 * `formTemplateFieldSchema.tableDataSource`'s element are both untyped on the wire, so anything
 * addressed by key off them has to be narrowed before it can be read.
 */
const isAnswerMap = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * One entry of a stored `users` answer, read as the {@link FormFlowUser} that `AddUser` seeds from.
 *
 * A stored entry is a denormalised ACCOUNT, not a flow user: this repo's own declaration of the
 * value is `FieldUserValue` (questionFieldsHelper.tsx:86) = `{ _id: string } & Partial<
 * UserTagProfileData>` — `_id`, no `id`. The cube-qa census agrees. Across every `users` question
 * and table cell (237 stored entries): 138 carry only `_id` (plus `fullName`), 99 carry `id` AND
 * `_id` holding the same value, none carries `id` alone, and all 237 resolve to a 24-hex id through
 * one key or the other. So BOTH keys are read here. Matching on `id` alone would hide 58% of every
 * existing assignment from the picker — and because `handleAddUserClick` (useForms.tsx:2004) writes
 * the picker's selection back as the WHOLE answer, re-saving such a question would delete those
 * users from the form for good.
 *
 * `fullName` is read for the same reason: it is the key the stored account shape keeps its display
 * name under, and `AddUser` renders the tag from `name`.
 */
const toFormFlowUser = (entry: unknown): FormFlowUser | undefined => {
  if (!isAnswerMap(entry)) return undefined;
  const id = objectIdString.safeParse(entry.id ?? entry._id);
  if (!id.success) return undefined;
  const name = entry.name ?? entry.fullName;
  return { id: id.data, name: typeof name === "string" ? name : undefined };
};

const toFormFlowUsers = (value: unknown): FormFlowUser[] =>
  Array.isArray(value)
    ? value.flatMap((entry) => {
        const user = toFormFlowUser(entry);
        return user ? [user] : [];
      })
    : [];

/**
 * A question as this component accepts it.
 *
 * Deliberately NOT `QuestionField` / `RenderableQuestionField`. The component has three unrelated
 * callers: `virtualisedSections` passes a live instance question (the Zod `questionFieldSchema`
 * shape), portal-settings' `formPreview` passes its own `PreviewQuestionField` (every member
 * optional, because a dozen call sites feed it partially-built question objects), and the
 * file-upload grid passes a synthesised custom field. Only `QuestionField` requires `_id` and
 * `fieldType`, so the three are not mutually assignable; this names only the members every caller
 * supplies, and the two hand-off points that need the Zod shape assert into it.
 */
type QuestionFieldInput = {
  _id?: string;
  fieldType?: string;
  fieldLabel?: string;
  questionValue?: string;
  isMandatory?: boolean;
  placeholderText?: string;
  answer?: { value?: unknown };
  viewType?: string;
  // Written by the form-template builder onto user-type questions only; `addUserModal` reads it to
  // pick single- vs multi-select. Not modelled by `questionFieldSchema` (see `UsersFieldData`).
  selectionType?: "singleUser" | "multipleUser";
  // Overloaded across field types: the four user scopes on a users question, the five date modes on
  // a date-time one. Stays `string` for that reason.
  selectionScope?: string;
  lookUpAnsFieldType?: string;
  minFieldRange?: number | null;
  maxFieldRange?: number | null;
  uploadSource?: string;
  openIn?: string;
  allowManualSelection?: boolean;
  allowedFileTypes?: QuestionField["allowedFileTypes"];
  allowCameraCaptureOnly?: boolean;
  /**
   * A template preset answer. Not modelled by `questionFieldSchema`, but its `.catchall(z.unknown())`
   * keeps whatever the builder stored, so this is `unknown` rather than typed out of existence.
   * Nothing writes it today (`questionFieldsHelper.tsx:51` has the investigation), so the
   * validate-on-mount effect below does not fire in practice — but declaring it `undefined`, as
   * `RenderableQuestionField` does, would make that effect statically dead while the read stays in
   * the emitted code, which is the opposite of what these rules are for.
   */
  defaultValue?: unknown;
};

/** The widest section shape all three callers satisfy. */
type SectionInput = {
  _id?: string;
  sectionCode?: string;
  tableDataSource?: unknown[];
  allowEditPreDefineRowInForm?: boolean;
};

export type FormQuestionFieldsProps = {
  eachField?: QuestionFieldInput;
  fieldIndex?: number;
  sectionIndex?: number;
  section?: SectionInput;
  source?: string;
  tableRowIndex?: number;
  tableRowId?: string | number | null;
  disabled?: boolean;
  showAnswersOnly?: boolean;
  formData?: { _id?: string; selectedForm?: unknown };
  userDetails?: { id?: string };
  extraParams?: Record<string, unknown>;
  templateSource?: string;
  spaceId?: string;
  width?: string;
  multiTextRows?: number;
  formValidator?: FieldValidatorHandle;
  isFileUploading?: Array<{ type?: string; uniqueId: string | number }>;
  modifySection?: Array<string | undefined>;

  handleInputChange?: (args: {
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    eachField?: QuestionFieldInput;
    section?: SectionInput;
    tableRowIndex?: number;
    source?: string;
  }) => void;
  handleChangeAnswer?: (args: {
    source?: string;
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    questionId?: string;
    tableRowIndex?: number;
  }) => void;
  handleCustomFieldData?:
    | ((args: {
        args?: Record<string, unknown>;
        eachField?: QuestionFieldInput;
        answerValue?: unknown;
        fieldIndex?: number | null;
        sectionIndex?: number | null;
        tableRowIndex?: number;
        source?: string;
      }) => void)
    | null;
  handleURLModalState?: (args: FieldModalStateArgs) => void;
  handleSaveUrlData?: (handleCustomFieldData: unknown, urlData: { URL?: string; displayText?: string }) => void;
  showUrlModal?: { state?: boolean; uniqueId?: string };
  showGeoLocationModal?: {
    state?: boolean;
    uniqueId?: string;
    /** Both flags are read by `AddGeoLocation`; the opener puts the whole question here. */
    eachField?: { allowManualSelection?: boolean; captureLiveLocation?: boolean };
  };
  setShowGeoLocationModal?: (args: { state: boolean }) => void;
  handleGeoLocationModalState?: (args: FieldModalStateArgs) => void;
  handleFileUploadChange?: (args: FileUploadChangeArgs) => void;
  handleRemoveUploadedFile?: (args: RemoveUploadedFileArgs) => void;
  handleRemoveAllUploadedFiles?: (args: RemoveAllUploadedFilesArgs) => void;
  removeUserFromList?: (args: RemoveUserFromListArgs) => void;
  saveEditedImage?: (args: SaveEditedImageArgs) => void;
  openAddUserModal?: (args: OpenAddUserModalArgs) => void;
  showAddUsersModal?: {
    status?: boolean;
    uniqueId?: string;
    source?: string;
    tableRowIndex?: number;
    fieldData?: { _id?: string; answer?: { value?: unknown } };
  };
  closeUsersModal?: () => void;
  handleAddUserClick?: (args: {
    handleCustomFieldData?: unknown;
    preSelectedUsers: unknown[];
    allSelectedUsers: unknown[];
  }) => void;
};

/**
 * What a widget hands back to `handleOnChange` / `handleOnBlur`. Every member is optional because
 * the shape is chosen by the widget, not by us: a DOM change event (`target`), a Dayjs instance
 * (`$d`), a tree-select selection (`itemData`), a File-Manager pick (`answer`), a geo point, or
 * `null` when a picker is cleared or a captured location removed.
 */
type FieldChangeArgs = {
  target?: { value?: unknown; checked?: boolean; name?: string };
  value?: unknown;
  /**
   * A File-Manager pick already merged with whatever the answer held before. Only the newly picked
   * entries are known to be {@link SelectedFormFile}s — the ones read back off the stored answer are
   * `unknown` (`answerSchema.value`) — and the merged array is only ever handed on as-is.
   */
  answer?: unknown[];
  itemData?: { id?: string; name?: string };
  /** A Dayjs instance's backing `Date`, which is what the date pickers hand back. */
  $d?: Date;
  current?: FieldGeoPoint | null;
  selected?: FieldGeoPoint | null;
  mapZoomLevel?: number;
} | null;

/** Outcome of translating a widget callback into a storable answer. */
type AnswerResult = { answerValue?: unknown; status: boolean; error?: TranslatedString };

const FormQuestionFields = (props: FormQuestionFieldsProps) => {
  const {
    eachField,
    fieldIndex,
    sectionIndex,
    section = {},
    source = formSectionType.FORM,
    tableRowIndex = 0,
    tableRowId = null,
    disabled = false,
    handleCustomFieldData = null,
    showAnswersOnly = false,
    formData = {},
    userDetails = {},
    handleInputChange,
    handleChangeAnswer,
    handleURLModalState,
    handleSaveUrlData,
    showUrlModal = {},
    spaceId,
    showGeoLocationModal = {},
    setShowGeoLocationModal,
    extraParams = {},
    templateSource,
    showAddUsersModal = {},
    closeUsersModal,
    handleAddUserClick,
    openAddUserModal,
    handleRemoveAllUploadedFiles,
  } = props;
  const params = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const lookUpQuery = new QueryBuilder().addParams("projectId", params.projectId);
  const userId = userDetails.id;
  const [teamsData, setTeamsData] = useState<Array<{ id: string; name: string }>>([]);
  const [locationData, setLocationData] = useState<Array<{ id: string; label: string; parentId?: string | null }>>([]);
  const [showFileManager, setShowFileManager] = useState(false);
  let formValidator: FieldValidatorHandle = props.formValidator || {};
  const tempFormValidator = useSelector((state: RootState) => state.formValidatorStore);
  if (!formValidator.validate) {
    formValidator = tempFormValidator;
  }
  // `eachField` has no default, so the destructure is on a possibly-absent object. The `!` is erased:
  // this component is only ever rendered for a question.
  const {
    _id,
    fieldType,
    lookUpAnsFieldType,
    minFieldRange,
    maxFieldRange,
    viewType = optionsViewType.LIST,
    selectionScope,
    defaultValue = null,
    uploadSource = fileUploadSources.LOCAL,
    openIn,
  } = eachField!;

  // Every hand-off from here down asserts the same way, for one reason: this component publishes
  // `QuestionFieldInput` / `SectionInput` / a `selectedForm?: unknown` form — the widest shapes its
  // three unrelated callers share (see `QuestionFieldInput`'s doc) — while every payload type
  // downstream is the Zod-strict question. The fix is upstream, in the declarations named on each
  // disabled line, not another shape squeezed in here.
  const {
    questionId,
    name,
    sectionId,
    currentAnswer,
    enabled,
    setOpenUrlPopUpState,
    openUrlPopUpState,
    formulaValue,
    concatenatedValue,
    // Bulk file actions: logic in the hook, UI here.
    handleDownloadAllFiles,
    isDownloadingAllFiles,
    handleDeleteAllFilesClick,
    isDeleteAllFilesModalOpen,
    closeDeleteAllFilesModal,
    confirmDeleteAllFiles,
    answerFiles,
  } = useQuestionFields({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `useQuestionFields` (useQuestionFields.tsx:259) declares `eachField?: Partial<QuestionField>`, i.e. the Zod `fieldType`/`viewType` enums, where this component publishes `QuestionFieldInput` with plain `string`s; it reads `_id`, `answer`, the `allow*` flags and `fieldType` equality, and forwards the object to `getFormulaFieldValue`/`getConcatenateFieldValue` (same file, :89) for `formulaExpression`/`fieldsVariableMapper`/`selectedFields`/`seperator`. Declaring those reads is the fix and that file is not this one
    eachField: eachField as Parameters<typeof useQuestionFields>[0]["eachField"],
    tableRowId,
    extraParams,
    section: section as Parameters<typeof useQuestionFields>[0]["section"],
    tableRowIndex,
    source,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `useQuestionFields` (useQuestionFields.tsx:267) declares `formData?: Partial<FormInstance>` — the whole stored instance, branded `_id` included — where this component's prop is `{ _id?: string; selectedForm?: unknown }`
    formData: formData as Parameters<typeof useQuestionFields>[0]["formData"],
    userId,
    disabled,
    showAnswersOnly,
    templateSource,
    fieldIndex,
    sectionIndex,
    handleCustomFieldData,
    saveRemoteFiles,
    handleRemoveAllUploadedFiles,
  });

  // Capture flow for `image` fields: camera-or-browse chooser (camera only when the template says
  // so), live capture, review, and hand-off of the confirmed photo into the regular upload path
  // through a synthetic change event — `handleFileUploadChange` only reads `e.target.files[0]`.
  const cameraCapture = useCameraCapture({
    cameraOnly: !!eachField?.allowCameraCaptureOnly,
    allowedFileTypes: eachField?.allowedFileTypes,
    onBrowse: () => document.getElementById(`${name}`)?.click(),
    onImageReady: ({ blob, type, name: fileName }) => {
      // The photo goes out as-is — the upload path sends the File's bytes as a binary
      // multipart part, so no base64 conversion happens anywhere.
      const file = new File([blob], fileName, { type });
      props.handleFileUploadChange?.({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `FileUploadChangeArgs.e` (questionFieldsHelper.tsx:201) is declared `React.ChangeEvent<HTMLInputElement>`, but the handler only reads `e.target.files[0]` and `e.target.value` (see the note above), which is exactly what this synthetic event carries; declaring `e` as that pair is the fix
        e: { target: { files: [file], value: "" } } as unknown as FileUploadChangeArgs["e"],
        sectionId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `FileUploadChangeArgs.eachField` (questionFieldsHelper.tsx:203) is the Zod-strict `RenderableQuestionField`; this component only ever holds a `QuestionFieldInput`
        eachField: eachField as FileUploadChangeArgs["eachField"],
        sectionIndex: sectionIndex!,
        fieldIndex: fieldIndex!,
        source,
        tableRowIndex,
        tableRowId,
        allowedFileTypes: eachField?.allowedFileTypes,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `FileUploadChangeArgs.formData` (questionFieldsHelper.tsx:166, `RendererFormData`) types `selectedForm`, which is `unknown` on this component's prop
        formData: formData as FileUploadChangeArgs["formData"],
        handleCustomFieldData,
        extraParams,
      });
    },
  });

  const getTeamsData = async (): Promise<void> => {
    const res = await executeQuery<TeamRow>(getProjectTeamsUrl, lookUpQuery);
    const modifiedData = Array.isArray(res.result)
      ? res.result.map((team: TeamRow) => ({
          id: team._id,
          name: team.name,
        }))
      : [];
    setTeamsData(modifiedData);
  };

  const getLocationData = async (): Promise<void> => {
    const res = await executeQuery<LocationRow>(locationUrl, lookUpQuery);
    const modifiedData = Array.isArray(res.result)
      ? res.result.map((item: LocationRow) => ({
          ...item,
          id: item._id,
          label: item.name,
          parentId: item.parentId ?? null,
        }))
      : [];
    setLocationData(modifiedData);
  };

  useEffect(() => {
    if (fieldType !== "look-up") return;
    if (lookUpAnsFieldType === "Teams" && params.projectId) {
      getTeamsData();
    }
    if (lookUpAnsFieldType === "Location" && params.projectId) {
      getLocationData();
    }
  }, [fieldType, lookUpAnsFieldType, params.projectId]);

  useEffect(() => {
    if (defaultValue) {
      formValidator.validate!({ name, value: defaultValue });
    }
  }, [defaultValue]);

  const getMultiSelectAnswer = (args: FieldChangeArgs) => {
    if (viewType === optionsViewType.LIST) {
      const prevAns: unknown[] = Array.isArray(currentAnswer.value) ? currentAnswer.value : [];
      return args!.target!.checked
        ? [...prevAns, args!.target!.name]
        : prevAns.filter((ans) => ans !== args!.target!.name);
    }
    return args!.target!.value;
  };

  const getDateTimeAnswer = (args: FieldChangeArgs): AnswerResult => {
    if (selectionScope === "currentDateTime") {
      return { answerValue: Date.now(), status: true };
    }
    let dateVal: number | null = null;
    if (args?.$d) {
      dateVal = new Date(args.$d).getTime();
    } else if (args?.value instanceof Date) {
      dateVal = args.value.getTime();
    }
    if (
      (minFieldRange && dateVal && dateVal < minFieldRange) ||
      (maxFieldRange && dateVal && dateVal > maxFieldRange)
    ) {
      // KNOWN BUG, preserved verbatim: this message has no locale key, so it is branded as
      // untranslated rather than translated. Adding the key is a behaviour change, not a typing one.
      return { answerValue: null, status: false, error: untranslated("Invalid date cannot be saved") };
    }
    if (Number.isNaN(dateVal)) {
      return { status: false };
    }
    return { answerValue: dateVal, status: true };
  };
  const getLookUpAnswer = (args: FieldChangeArgs) => {
    if (lookUpAnsFieldType === "Teams") return args;

    if (lookUpAnsFieldType === "Location") {
      const prevAns: unknown[] = Array.isArray(currentAnswer.value) ? currentAnswer.value : [];
      return [...prevAns, { id: args!.itemData!.id, name: args!.itemData!.name }];
    }

    return currentAnswer.value || [];
  };

  const getFileUploadAnswer = (args: FieldChangeArgs, currentAnswer?: { value?: unknown }) =>
    Array.isArray(currentAnswer?.value) ? [...currentAnswer.value, ...args!.answer!] : [...args!.answer!];

  const getAnswer = (args: FieldChangeArgs): AnswerResult => {
    let output: AnswerResult = { answerValue: "", status: true };

    // Every `args!.target!` below is an erased assertion. `args` is `null` only on the geoLocation
    // and date-time clear paths, and both land in their own branches — which is why the original
    // code could read `.target.value` unguarded here.
    if (fieldType !== undefined && ["single-line", "multi-line", "single-select"].includes(fieldType)) {
      output.answerValue = args!.target!.value;
    } else if (fieldType == "multi-select") {
      output.answerValue = getMultiSelectAnswer(args);
    } else if (fieldType == "number") {
      // The widget decides the shape it hands back (see `FieldChangeArgs`); a number input's is the
      // DOM string, which is what the `.replace` has always assumed.
      const rawValue = String(args!.target!.value).replace(/,/g, "");
      output.answerValue = rawValue === "" ? null : Number(rawValue);
    } else if (fieldType == "date-time") {
      const result = getDateTimeAnswer(args);
      if (!result.status) {
        return result;
      }
      output.answerValue = result.answerValue;
    } else if (fieldType == "look-up") {
      output.answerValue = getLookUpAnswer(args);
    } else if (fieldType == "fileUpload" && uploadSource === fileUploadSources.FILE_MANAGER) {
      output.answerValue = getFileUploadAnswer(args);
    } else if (fieldType == "geoLocation") {
      output.answerValue = args;
    }
    return output;
  };

  const handleOnBlur = (args: FieldChangeArgs) => {
    const res = getAnswer(args);
    if (res.status) {
      if (formData._id) {
        handleInputChange!({
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          eachField,
          section,
          tableRowIndex,
          source,
        });
      }
    } else {
      dispatch(
        showToastNotification({
          // `getAnswer` rejects a NaN date without a reason of its own, and a toast with no body
          // renders an empty bubble.
          content: res.error ?? SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const handleOnChange = (args: FieldChangeArgs) => {
    const res = getAnswer(args);

    if (formValidator.validate) {
      formValidator.validate({ name, value: res.answerValue });
    }
    if (res.status) {
      if (handleChangeAnswer) {
        handleChangeAnswer({
          source,
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          questionId,
          tableRowIndex,
        });
      }
      if (handleCustomFieldData) {
        handleCustomFieldData({
          args: extraParams,
          eachField,
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          tableRowIndex,
          source,
        });
      }
    }
  };

  const handleFileUploadClick = () => {
    if (uploadSource == fileUploadSources.LOCAL) {
      document.getElementById(`${name}`)?.click();
    } else {
      setShowFileManager(true);
    }
  };

  const handleUrlFieldClick = () => {
    const url = getUrlFromField(_.get(currentAnswer, "value.URL", ""));
    if (openIn == "popUp") {
      setOpenUrlPopUpState({ state: true, url, uniqueId: name });
    } else if (openIn == "sameTab") {
      window.location.href = url;
    } else {
      window.open(url);
    }
  };

  const handleRemoveLocation = () => {
    handleOnChange(null);
    handleOnBlur(null);
  };

  const getFieldAnsComponent = () => {
    const renderer = fieldType ? fieldRenderers[fieldType] : undefined;
    // `fieldRenderers` is keyed by `fieldType` and each entry declares the concrete answer shape
    // that fieldType stores, so no single props type the table exposes can be both callable and
    // assignable from all fifteen renderers; `never` is the value type that makes the table
    // assignment work, and every call site asserts into it.
    const fieldComponent = renderer ? (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `fieldRenderers` (questionFieldsHelper.tsx:1195) is `Record<string, (p: QuestionFieldRenderProps<never>) => ReactNode>`, and `<never>` makes `currentAnswer.value` unsatisfiable by construction — a per-fieldType discriminated union on the table is the fix
      renderer({
        ...props,
        projectId: params.projectId,
        formValidator,
        questionId,
        name,
        sectionId,
        currentAnswer,
        enabled,
        formulaValue,
        concatenatedValue,
        handleOnChange,
        handleOnBlur,
        handleRemoveLocation,
        lookUpQuery,
        teamsData,
        locationData,
        handleFileUploadClick,
        handleUrlFieldClick,
        openAddUserModal,
        handleDownloadAllFiles,
        isDownloadingAllFiles,
        handleDeleteAllFilesClick,
        openImageUploadOptions: cameraCapture.openImageCapture,
        formData,
        source,
      } as QuestionFieldRenderProps<never>)
    ) : (
      <></>
    );

    const showCustomValidation =
      fieldType !== undefined && ["look-up", "users", "fileUpload", "image", "url", "geoLocation"].includes(fieldType);

    return showCustomValidation ? (
      <ValidationWrapperWeb value={currentAnswer.value} name={name} validator={formValidator}>
        {fieldComponent}
      </ValidationWrapperWeb>
    ) : (
      <FormControl fullWidth error={!!_.get(formValidator, `error.${name}`)}>
        {fieldComponent}
        {!!_.get(formValidator, `error.${name}`) && (
          <FormHelperText sx={{ marginInlineStart: 0 }}>{_.get(formValidator, `error.${name}`)}</FormHelperText>
        )}
      </FormControl>
    );
  };

  const handleFileSelectedCancelClick = () => {
    setShowFileManager(false);
  };

  const handleFileSelectedButtonClick = (args: Record<string, FileManagerSelection>) => {
    // Each `!` is erased. These five members are optional on a File-Manager entry in general, but a
    // file the picker returns as selected always carries them — which is what the original
    // unannotated reads assumed.
    const selectedFiles: SelectedFormFile[] = Object.values(args).map((file: FileManagerSelection) => ({
      fileVersion: file.fileVersion!,
      fileId: file.hierarchyId!,
      resourceType: file.resourceType!,
      versionNumber: file.versionNumber!,
      name: file.name!,
    }));
    const previousFiles: unknown[] = Array.isArray(currentAnswer.value) ? currentAnswer.value : [];
    const answer = [...previousFiles, ...selectedFiles];
    handleOnChange({ answer });
    handleOnBlur({ answer });
    handleFileSelectedCancelClick();
  };

  const handleSaveLocation = ({
    current,
    selected,
    mapZoomLevel,
  }: {
    current: FieldGeoPoint | null;
    selected: FieldGeoPoint | null;
    mapZoomLevel: number;
  }) => {
    if (showGeoLocationModal.eachField?.allowManualSelection) {
      handleOnChange({ current, selected, mapZoomLevel });
      handleOnBlur({ current, selected, mapZoomLevel });
    } else {
      handleOnChange({ current, selected: current, mapZoomLevel });
      handleOnBlur({ current, selected: current, mapZoomLevel });
    }
    setShowGeoLocationModal!({ state: false });
  };
  /**
   * The users already on this question, which is what `AddUser` seeds its selection from. A table
   * section keeps the answer on the row rather than on the question, and both are untyped on the
   * wire — an answer this cannot read back as a list of users is an empty selection, which is what
   * the `?? []` fallbacks on the original reads produced.
   */
  const getAssignedUsers = (): FormFlowUser[] => {
    if (showAddUsersModal.source !== formSectionType.TABLE_SECTION) {
      return toFormFlowUsers(showAddUsersModal.fieldData?.answer?.value);
    }
    const questionId = showAddUsersModal.fieldData?._id;
    const rowIndex = showAddUsersModal.tableRowIndex;
    if (questionId === undefined || rowIndex === undefined) return [];
    const row = section.tableDataSource?.[rowIndex];
    if (!isAnswerMap(row)) return [];
    const cell = row[questionId];
    return toFormFlowUsers(isAnswerMap(cell) ? cell.value : undefined);
  };

  const deleteAllFilesPopup = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent onClick={confirmDeleteAllFiles} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeDeleteAllFilesModal}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      headerTitle={
        <div className="w-full flex justify-between rounded-md">
          <Text>{t("Delete all files")}</Text>
        </div>
      }
      isVisible
      onCancel={closeDeleteAllFilesModal}
      modelContent={
        <Text className="whitespace-normal">
          {t("This will permanently remove all {{count}} file(s) from this field. Are you sure ?", {
            count: answerFiles.length,
          })}
        </Text>
      }
      width="30%"
    />
  );

  return (
    <>
      {showAnswersOnly
        ? getFieldAns(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `getFieldAns` (questionFieldsHelper.tsx:1237) takes `FieldAnswerViewProps`, whose `eachField` is the Zod-strict `RenderableQuestionField`; this component's props publish `QuestionFieldInput`
            props as FieldAnswerViewProps,
            currentAnswer,
            handleUrlFieldClick,
            params.projectId,
            handleDownloadAllFiles,
          )
        : getFieldAnsComponent()}
      {isDeleteAllFilesModalOpen && deleteAllFilesPopup()}
      {showFileManager && (
        <FileManagerAsPopup
          projectId={params.projectId}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileSelectedCancelClick}
          workspaceId={spaceId}
          selectedFilesData={currentAnswer.value}
          hideIssues
          hideTasks
          hideEstimates
          hideSchedules
          hideContracts
          hideExternalSpace
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_MERGED_FILE"]}
        />
      )}
      {cameraCapture.captureState !== "closed" && <CameraCaptureModals capture={cameraCapture} />}
      {/* `AddURLModal` calls both handlers unguarded, and the only thing that can open it is
          `handleURLModalState` itself — so a caller that left them out cannot reach this anyway. */}
      {showUrlModal.state && name === showUrlModal.uniqueId && handleURLModalState && handleSaveUrlData && (
        <AddURLModal
          showUrlModal={showUrlModal}
          handleCustomFieldData={handleCustomFieldData}
          handleURLModalState={handleURLModalState}
          handleSaveUrlData={handleSaveUrlData}
          name={name}
        />
      )}
      {/* `AddGeoLocation` reads `showGeoLocationModal.eachField` with no guard, so the modal is
          rendered only once the opener has actually put the question on the state. */}
      {showGeoLocationModal.state && name == showGeoLocationModal.uniqueId && showGeoLocationModal.eachField && (
        <AddGeoLocation
          setShowGeoLocationModal={setShowGeoLocationModal!}
          showGeoLocationModal={{
            state: showGeoLocationModal.state,
            uniqueId: showGeoLocationModal.uniqueId,
            eachField: showGeoLocationModal.eachField,
          }}
          handleSaveLocation={handleSaveLocation}
          name={name}
        />
      )}
      {openUrlPopUpState.state && openUrlPopUpState.uniqueId === name && (
        <ModalStateDialogComponent
          headerTitle={<div className="flex justify-between w-full">{t("View Url")}</div>}
          modelContent={<EmbeddedViewer id={_id} embedUrl={openUrlPopUpState.url} />}
          width="95%"
          height="95%"
          onCancel={() => setOpenUrlPopUpState({ state: false, url: "" })}
          isVisible={openUrlPopUpState.state}
        />
      )}
      {showAddUsersModal.status && name === showAddUsersModal.uniqueId && (
        <AddUser
          currentUsers={getAssignedUsers()}
          closeUsersModal={closeUsersModal!}
          handleAddUserClick={handleAddUserClick!}
          fieldData={eachField}
          section={section}
          // The three ids below came out of Mongo — the route param, the loaded instance, and the
          // space the form lives in — so they are re-tagged rather than re-validated.
          // `toObjectIdString` is the sanctioned way to do that (it maps `undefined` through), and
          // is what keeps a bare `as ObjectIdString` out of this file.
          formId={toObjectIdString(formData._id)}
          spaceId={toObjectIdString(spaceId)}
          projectId={toObjectIdString(params.projectId)}
          handleCustomFieldData={handleCustomFieldData}
        />
      )}
    </>
  );
};
export default FormQuestionFields;
